import * as React from 'react';
/**
 * Open state for a primitive wrapper that must be able to open or close its
 * Radix Root itself (touch-aware DropdownMenu and Tooltip), while still
 * honouring a caller that controls `open` / `onOpenChange`.
 */
export function useControllableOpen({ open: openProp, defaultOpen, onOpenChange, }) {
    const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen ?? false);
    const isControlled = openProp !== undefined;
    const open = isControlled ? openProp : uncontrolledOpen;
    // Read through refs so the setter stays stable across renders.
    const onOpenChangeRef = React.useRef(onOpenChange);
    const isControlledRef = React.useRef(isControlled);
    const committedRef = React.useRef(open);
    // What the requests made so far will render. A request moves it at once
    // rather than after the re-render, so a second request in the same task is
    // measured against the first: focus handed back to a trigger opens its
    // tooltip, and a dialog taking focus straight after must still close it,
    // not be dropped as "already closed".
    const latestRef = React.useRef(open);
    React.useLayoutEffect(() => {
        onOpenChangeRef.current = onOpenChange;
        isControlledRef.current = isControlled;
        committedRef.current = open;
        latestRef.current = open;
    });
    const setOpen = React.useCallback((update) => {
        const next = typeof update === 'function' ? update(latestRef.current) : update;
        if (next === latestRef.current)
            return;
        latestRef.current = next;
        if (isControlledRef.current) {
            // A controlling parent may turn the request down without re-rendering,
            // so the guess only lasts for the rest of this task.
            queueMicrotask(() => {
                latestRef.current = committedRef.current;
            });
        }
        else {
            setUncontrolledOpen(next);
        }
        onOpenChangeRef.current?.(next);
    }, []);
    return [open, setOpen];
}
